import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Container, PartyPopper, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { usePermission, useTenant } from "@/components/AppShell";
import { Card, cx, Skeleton } from "@/components/ui";
import { api, type CalendarEvent } from "@/lib/api";
import { intlLocale, t } from "@/lib/i18n";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
/** heute in deutscher Zeit */
const todayYmd = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const dayLabel = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString(intlLocale(), { weekday: "short", day: "2-digit", month: "2-digit" });

/** Übersicht: Feiertage an den Lagerstandorten, feste Anliefertermine und erwartete Anlieferungen */
export function CalendarCard() {
  const { tenantId, me } = useTenant();
  const seeInbounds = usePermission()("inbounds", "view");
  const today = todayYmd();
  const [month, setMonth] = useState(() => ({ y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)) - 1 }));
  const [picked, setPicked] = useState<string | null>(null);
  const days = new Date(Date.UTC(month.y, month.m + 1, 0)).getUTCDate();
  const from = ymd(month.y, month.m, 1), to = ymd(month.y, month.m, days);
  const q = useQuery({ queryKey: ["calendar", tenantId, from], queryFn: () => api.calendar(tenantId, from, to), placeholderData: keepPreviousData, staleTime: 5 * 60_000 });
  const events = useMemo(() => (q.data?.events ?? []).filter((e) => seeInbounds || e.type === "holiday"), [q.data, seeInbounds]);
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarEvent[]>();
    for (const e of events) m.set(e.date, [...(m.get(e.date) ?? []), e]);
    return m;
  }, [events]);
  const lead = (new Date(Date.UTC(month.y, month.m, 1)).getUTCDay() + 6) % 7;
  const shift = (n: number) => { setPicked(null); setMonth(({ y, m }) => { const d = new Date(Date.UTC(y, m + n, 1)); return { y: d.getUTCFullYear(), m: d.getUTCMonth() }; }); };
  // Liste: gewählter Tag – sonst alles ab heute in diesem Monat (bzw. der ganze Monat, wenn er nicht der laufende ist)
  const agenda = picked ? byDay.get(picked) ?? [] : events.filter((e) => e.date >= (today.slice(0, 7) === from.slice(0, 7) ? today : from));
  const missing = q.data?.sites.filter((s) => !s.region) ?? [];
  const holidayAt = (e: CalendarEvent) => e.type === "appointment" && (byDay.get(e.date) ?? []).some((h) => h.type === "holiday" && (h.allWarehouses || !e.warehouse || h.warehouses.includes(e.warehouse)));

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-semibold flex items-center gap-2"><CalendarDays className="size-4 text-fg-muted" /> {t("Kalender")}</h2>
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => shift(-1)} className="size-7 grid place-items-center rounded-md hover:bg-surface-2" aria-label={t("Vorheriger Monat")}><ChevronLeft className="size-4" /></button>
          <span className="text-[13px] font-medium w-28 text-center tabular-nums">{MONTHS[month.m]} {month.y}</span>
          <button onClick={() => shift(1)} className="size-7 grid place-items-center rounded-md hover:bg-surface-2" aria-label={t("Nächster Monat")}><ChevronRight className="size-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] text-fg-subtle mb-1">{DOW.map((d) => <span key={d}>{d}</span>)}</div>
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: lead }, (_, i) => <span key={`l${i}`} />)}
        {Array.from({ length: days }, (_, i) => {
          const d = ymd(month.y, month.m, i + 1);
          const ev = byDay.get(d) ?? [];
          const holiday = ev.some((e) => e.type === "holiday");
          const weekend = (lead + i) % 7 >= 5;
          return (
            <button
              key={d}
              onClick={() => setPicked(picked === d ? null : d)}
              aria-label={`${dayLabel(d)}${ev.length ? `: ${ev.length} Einträge` : ""}`}
              className={cx(
                "relative h-9 rounded-lg text-[12.5px] tabular-nums transition-colors",
                picked === d ? "bg-ink text-ink-fg" : d === today ? "bg-surface-2 font-semibold ring-1 ring-border-strong" : "hover:bg-surface-2",
                picked !== d && (holiday ? "text-danger font-medium" : weekend ? "text-fg-subtle" : ""),
              )}
            >
              {i + 1}
              {ev.length > 0 && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                  {holiday && <span className="size-1 rounded-full bg-danger" />}
                  {ev.some((e) => e.type === "appointment") && <span className="size-1 rounded-full bg-info" />}
                  {ev.some((e) => e.type === "expected") && <span className="size-1 rounded-full bg-fg-subtle" />}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-fg-subtle">
        <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-danger" /> {t("Feiertag im Lager")}</span>
        {seeInbounds && <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-info" /> {t("Anliefertermin")}</span>}
        {seeInbounds && <span className="inline-flex items-center gap-1"><span className="size-1.5 rounded-full bg-fg-subtle" /> {t("Anlieferung erwartet")}</span>}
      </div>

      <div className="mt-3 border-t border-border pt-3">
        <p className="text-[11.5px] font-medium text-fg-subtle mb-1.5">{picked ? dayLabel(picked) : t("Demnächst")}</p>
        {q.isLoading ? <Skeleton className="h-10" /> : agenda.length === 0 ? (
          <p className="text-[12.5px] text-fg-muted">{picked ? t("Nichts eingetragen.") : t("In diesem Monat steht nichts mehr an.")}</p>
        ) : (
          <ul className="space-y-2">
            {agenda.slice(0, picked ? 20 : 6).map((e, i) => <AgendaItem key={i} e={e} showDate={!picked} holidayClash={holidayAt(e)} />)}
          </ul>
        )}
      </div>
      {me.user.isStaff && missing.length > 0 && (
        <p className="mt-3 text-[11.5px] text-warning">{t("Für")} {missing.map((s) => s.name).join(", ")} {t("ist kein Bundesland hinterlegt – keine Feiertage. Logiful intern → Kunden → Lagerstandorte (Bundesland festlegen oder Adresse ergänzen).")}</p>
      )}
    </Card>
  );
}

function AgendaItem({ e, showDate, holidayClash }: { e: CalendarEvent; showDate: boolean; holidayClash: boolean }) {
  const date = showDate && <span className="text-fg-subtle tabular-nums mr-1.5">{dayLabel(e.date)}</span>;
  if (e.type === "holiday")
    return (
      <li className="flex items-start gap-2 text-[12.5px]">
        <PartyPopper className="size-3.5 text-danger mt-0.5 shrink-0" />
        <span>{date}<b className="font-medium">{e.name}</b><span className="block text-[11.5px] text-fg-muted">{t("Feiertag")} {e.allWarehouses ? t("im Lager") : t("in {v}", { v: e.warehouses.join(", ") })} {t("– kein Versand, kein Wareneingang")}</span></span>
      </li>
    );
  const to = `/inbounds?q=${encodeURIComponent(e.number)}`;
  if (e.type === "appointment")
    return (
      <li className="flex items-start gap-2 text-[12.5px]">
        <Container className="size-3.5 text-info mt-0.5 shrink-0" />
        <span>
          {date}<b className="font-medium tabular-nums">{e.from}{e.to ? `–${e.to}` : ""} {t("Uhr")}</b> · <Link to={to} className="hover:underline">{e.number}</Link>
          <span className="block text-[11.5px] text-fg-muted">{[e.note, e.warehouse].filter(Boolean).join(" · ") || t("Fester Anliefertermin")}</span>
          {holidayClash && <span className="flex items-center gap-1 text-[11.5px] text-danger"><AlertTriangle className="size-3" /> {t("Termin liegt auf einem Feiertag")}</span>}
        </span>
      </li>
    );
  return (
    <li className="flex items-start gap-2 text-[12.5px]">
      <Truck className="size-3.5 text-fg-subtle mt-0.5 shrink-0" />
      <span>{date}{t("Anlieferung erwartet ·")} <Link to={to} className="hover:underline">{e.number}</Link>{e.warehouse && <span className="block text-[11.5px] text-fg-muted">{e.warehouse} {t("· ohne festen Termin")}</span>}</span>
    </li>
  );
}
